import { Component, ErrorInfo, ReactNode } from 'react';
import { reportClientError } from '../utils/reportClientError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary global pour capturer les erreurs React
 * et empêcher le crash complet de l'application
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null
    };
  }

  /**
   * Une erreur vient-elle d'une extension de navigateur ?
   *
   * On ne se fie qu'à l'ORIGINE du code fautif dans la pile. L'ancien filtre
   * regardait aussi le message et ignorait tout ce qui contenait « onboarding »
   * ou « extension » : nos propres erreurs du parcours d'inscription étaient
   * donc avalées sans laisser de trace, et l'utilisateur restait devant une
   * page à moitié rendue sans comprendre pourquoi.
   */
  private static isBrowserExtensionError(error: Error): boolean {
    const stack = error.stack || "";
    return (
      stack.includes("chrome-extension://") ||
      stack.includes("moz-extension://") ||
      stack.includes("safari-extension://")
    );
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (ErrorBoundary.isBrowserExtensionError(error)) {
      console.warn("[Wanzo] Erreur provenant d'une extension, ignorée:", error.message);
      return { hasError: false, error: null };
    }

    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[Wanzo] Erreur capturée par ErrorBoundary:", error);
    console.error("[Wanzo] Info composant:", errorInfo.componentStack);

    // Remonter au backend : une erreur qui ne sort jamais du navigateur du
    // client est une erreur que personne ne corrigera.
    if (!ErrorBoundary.isBrowserExtensionError(error)) {
      reportClientError({
        message: error.message || String(error),
        stack: error.stack,
        componentStack: errorInfo.componentStack ?? undefined,
      });
    }

    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Effacer le cache et recharger
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // UI de fallback personnalisée
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Cette page n'a pas pu s'afficher
            </h2>
            
            <p className="text-gray-600 mb-6">
              Cette page n'a pas pu s'afficher correctement. Vos données enregistrées ne sont pas affectées, et le problème vient de nous être signalé.
            </p>

            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Recharger la page
              </button>
              
              <button
                onClick={this.handleReset}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Réessayer
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Détails techniques (dev)
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <p className="mt-6 text-xs text-gray-400">
              Si le problème persiste, réessayez dans quelques minutes ou contactez le support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
